// @flow
import * as React from 'react';
import {useEffect, useRef} from 'react';
import * as Prism from 'prismjs';
import classNames from 'classnames';

import {useStep, useFileRef} from 'docs/data/integration-builder';
import './Line.scss';

type Position = 'first' | 'last' | null;
export type LinePosition = {[string]: Position};

type LineProps = {|
  steps: Array<string>,
  language: string,
  content: string,
  toggled: boolean,
  position: LinePosition,
|};

export const Line = ({
  steps,
  language,
  content,
  toggled,
  position,
}: LineProps) => {
  const {activeStep} = useStep();
  const ref = useRef();
  const {getScrollPosition, setScrollPosition, getHeight} = useFileRef();

  useEffect(() => {
    if (!activeStep || !steps.includes(activeStep)) {
      return;
    }
    const isFirstLineInStep = position[activeStep] === 'first';
    const isLastLineInStep = position[activeStep] === 'last';
    // Check whether we need to scroll the <pre> el
    // to center the active lines in the view
    const pos = getScrollPosition();
    const height = getHeight();
    const offset = ref?.current?.offsetTop;
    if (
      pos === -1 ||
      height === -1 ||
      offset === undefined ||
      !ref?.current?.offsetParent
    ) {
      return;
    }

    // Only check for position if it's the first or last line in the codeblock
    if (isLastLineInStep) {
      if (offset <= pos || height + pos <= offset + 20) {
        // Only scroll far enough to reveal the lines that are hidden
        setScrollPosition(offset - height + 40);
      }
    } else if (isFirstLineInStep) {
      if (offset <= pos || height <= offset + 20) {
        // Scroll to where the code snippet starts
        setScrollPosition(offset - 20);
      }
    }
  }, [
    ref,
    steps,
    activeStep,
    position,
    setScrollPosition,
    getScrollPosition,
    getHeight,
  ]);

  useEffect(() => {
    // Need to highlight each line element to preserve the wrapping `Line` span
    // otherwise Prism removes them during tokenization
    Prism.highlightElement(ref.current);
  }, [content]);

  return (
    <div>
      {/* extra div needed for https://stackoverflow.com/questions/54880669/react-domexception-failed-to-execute-removechild-on-node-the-node-to-be-re */}
      <span
        className={classNames(
          'Line',
          steps.includes(activeStep) ? 'Line-active' : 'Line-faded',
          toggled && 'Line-toggled',
        )}
      >
        <span className="Line-number" />
        <span ref={ref} className={`language-${language}`}>
          {content}
        </span>
      </span>
    </div>
  );
};
